import { supabase, isConfigured } from "@/lib/supabase"
import type { Lead, LeadStatus, LeadOrigem } from "@/mocks/admin"

export const leadsService = {
  async getAll(): Promise<Lead[]> {
    if (!isConfigured) return []
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .order("data_cadastro", { ascending: false })
    if (error || !data) return []
    return data.map(rowToLead)
  },

  async update(id: string, updates: Partial<Lead>): Promise<Lead | null> {
    console.log('[leadsService.update] isConfigured:', isConfigured, 'supabaseUrl:', (supabase as any).supabaseUrl)
    if (!isConfigured) return null
    const payload = rowFromLead(updates as Lead)
    console.log('[leadsService.update] id:', id, 'payload:', payload)
    const { data, error } = await supabase
      .from("leads")
      .update(payload)
      .eq("id", id)
      .select()
      .single()
    if (error) {
      console.error('[leadsService.update] error:', error)
      return null
    }
    if (!data) {
      console.warn('[leadsService.update] no data returned')
      return null
    }
    return rowToLead(data)
  },

  async updateStatus(id: string, status: LeadStatus): Promise<Lead | null> {
    console.log('[leadsService.updateStatus] id:', id, 'status:', status)
    return this.update(id, { status } as any)
  },

  async assignTurma(id: string, turmaId: string | null): Promise<Lead | null> {
    return this.update(id, { turmaId } as any)
  },

  async delete(id: string): Promise<boolean> {
    if (!isConfigured) return false
    const { error } = await supabase.from("leads").delete().eq("id", id)
    return !error
  },

  /** Unifica lead por email — se já existe, adiciona origem e atualiza dados. Se não, cria novo. */
  async upsertByEmail(leadData: {
    nome: string;
    email: string;
    whatsapp: string;
    empresa: string;
    origem: LeadOrigem;
    diagnostico?: Lead["diagnostico"];
    segmento?: string;
    maior_desafio?: string;
  }): Promise<Lead | null> {
    if (!isConfigured) return null

    // Busca lead existente pelo email
    const { data: existente } = await supabase
      .from("leads")
      .select("*")
      .eq("email", leadData.email)
      .maybeSingle()

    if (existente) {
      // Adiciona origem se ainda não tiver
      const origensAtuais: string[] = existente.origens ?? []
      const novaOrigem = leadData.origem
      const origensUnicas = origensAtuais.includes(novaOrigem)
        ? origensAtuais
        : [...origensAtuais, novaOrigem]

      const updates: Partial<Lead> = {
        origens: origensUnicas as LeadOrigem[],
        nome: leadData.nome || existente.nome,
        whatsapp: leadData.whatsapp || existente.whatsapp,
        empresa: leadData.empresa || existente.empresa,
        segmento: (leadData as any).segmento || existente.segmento || "",
        maior_desafio: (leadData as any).maior_desafio || existente.maior_desafio || "",
      }
      // Se veio diagnostico, atualiza tb
      if (leadData.diagnostico) {
        updates.diagnostico = leadData.diagnostico
      }

      return this.update(existente.id, updates)
    } else {
      // Cria novo lead com array de origens
      const { data, error } = await supabase
        .from("leads")
        .insert({
          nome: leadData.nome,
          email: leadData.email,
          whatsapp: leadData.whatsapp,
          empresa: leadData.empresa,
          segmento: leadData.segmento || "",
          maior_desafio: leadData.maior_desafio || "",
          origens: [leadData.origem],
          status: "novo",
          diagnostico: leadData.diagnostico ?? null,
        })
        .select()
        .single()
      if (error || !data) return null
      return rowToLead(data)
    }
  },
}

function rowToLead(row: any): Lead {
  return {
    id: row.id,
    nome: row.nome,
    email: row.email,
    whatsapp: row.whatsapp,
    empresa: row.empresa,
    segmento: row.segmento ?? "",
    maior_desafio: row.maior_desafio ?? "",
    origens: (row.origens ?? ["diagnostico"]) as LeadOrigem[],
    status: row.status as LeadStatus,
    turmaId: row.turma_id ?? null,
    dataCadastro: row.data_cadastro,
    diagnostico: row.diagnostico ?? null,
    forma_pagamento: row.forma_pagamento ?? undefined,
    num_parcelas: row.num_parcelas ?? undefined,
    valor_subsidio: row.valor_subsidio ?? undefined,
    valor_entrada: row.valor_entrada ?? undefined,
    preco_programa: row.preco_programa ?? undefined,
    porte: row.porte ?? undefined,
    tempo_mercado: row.tempo_mercado ?? undefined,
    num_colaboradores: row.num_colaboradores ?? undefined,
    faturamento: row.faturamento ?? undefined,
    desafio: row.desafio ?? undefined,
  }
}

function rowFromLead(lead: Partial<Lead>): any {
  return {
    nome: lead.nome,
    email: lead.email,
    whatsapp: (lead.whatsapp || "").replace(/\D/g, ""),
    empresa: lead.empresa,
    segmento: lead.segmento ?? "",
    maior_desafio: lead.maior_desafio ?? "",
    origens: lead.origens,
    status: lead.status,
    turma_id: lead.turmaId,
    diagnostico: lead.diagnostico,
    forma_pagamento: lead.forma_pagamento,
    num_parcelas: lead.num_parcelas,
    valor_subsidio: lead.valor_subsidio,
    valor_entrada: lead.valor_entrada,
    preco_programa: lead.preco_programa,
    porte: lead.porte,
    tempo_mercado: lead.tempo_mercado,
    num_colaboradores: lead.num_colaboradores,
    faturamento: lead.faturamento,
    desafio: lead.desafio,
  }
}
